import React, { Component, Fragment, lazy } from 'react';

import { Accordion } from 'components/accordion';
import { Grid } from 'components/grid';
import { InputBlock } from 'components/input';
import { InputGroup } from 'components/input/group';
import { Panel } from 'components/panel';
import { Radiolist } from 'components/radiolist';
import { I18n, translate } from 'components/translate';
import { rawTextLength } from 'helpers/String';
import withSuspense from 'hoc/withSuspense';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import Dripping from 'modules/content/components/dripping/Dripping';
import Language from 'modules/content/components/language/Language';
import DrippingList from 'modules/content/dripping/components/drippingSection';
import { PageType } from 'modules/content/moduleService/ModuleTypes';
import { QuizService } from 'modules/content/quiz';
import { FeatureToggles } from 'modules/featureToggle';
import PropTypes from 'prop-types';

import DrippingValidation from '../../components/dripping/DrippingValidation';

const ContentEditorUploadWrapper = withSuspense(
  lazy(() => import('components/editor/ContentUploadWrapper'))
);

@translate()
@inject((stores) => ({
  QuizStore: stores.QuizStore,
  isDrippingByClassEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.DrippingByClass
  )
}))
@observer
export default class QuizConfigs extends Component {
  static propTypes = {
    t: PropTypes.func,
    QuizStore: MobxPropTypes.objectOrObservableObject,
    maxDescriptionLen: PropTypes.number,
    fieldErrorName: PropTypes.bool,
    changeFieldError: PropTypes.func,
    languageError: PropTypes.bool,
    drippingErrors: PropTypes.object
  };

  static defaultProps = {
    drippingErrors: DrippingValidation.initialState
  };

  quizTypes = [
    {
      value: 'EXERCISE',
      name: this.props.t('general.exercise'),
      description: this.props.t('quiz_editor.exercise_description')
    },
    {
      value: 'PROFILE',
      name: this.props.t('general.profile'),
      description: this.props.t('quiz_editor.profile_description')
    }
  ];

  changeQuizType = (e) => {
    QuizService.type = e.target.value;
  };

  changeName = (e) => {
    if (!QuizService.name && e.target.value) {
      const fieldErrorName = 'fieldErrorName';
      const errorState = false;

      this.props.changeFieldError(fieldErrorName, errorState);
    }

    QuizService.name = e.target.value;
  };

  onContentChange = (e) => {
    const { value } = e.target;

    QuizService.content = value;
  };

  changeLiberationDays = (e) => {
    QuizService.liberationDays = e.target.value;
  };

  changeLiberationType = (liberationType) => {
    QuizService.liberationType = liberationType;
  };

  changeHasDuration = (hasDuration) => {
    QuizService.hasDuration = hasDuration;
  };

  changeLiberationDate = (liberationDate) => {
    QuizService.liberationDate = liberationDate;
  };

  changeDurationDays = (durationDays) => {
    QuizService.durationDays = durationDays;
  };

  changeLanguage = (language) => {
    QuizService.language = language;
  };

  changeSignLanguage = (signLanguage) => {
    QuizService.signLanguage = signLanguage;
  };

  getDescription(maxDescriptionLen, remainingCaracteres) {
    return (
      <label id="description">
        <I18n label="general.description" />

        {maxDescriptionLen && maxDescriptionLen > 0 && (
          <span
            className={
              remainingCaracteres < 0
                ? 'char__limit char__limit_exceed'
                : 'char__limit'
            }
          >
            {`${remainingCaracteres} `}
            <I18n label="general.characters" />
          </span>
        )}
      </label>
    );
  }

  render() {
    const { type, page } = this.props.QuizStore;
    const {
      maxDescriptionLen,
      fieldErrorName,
      languageError,
      drippingErrors,
      isDrippingByClassEnabled
    } = this.props;
    const rawContentLength = rawTextLength(page.content || '');
    const remainingCaracteres = maxDescriptionLen - rawContentLength;
    const inputErrorClass = fieldErrorName ? 'input__group__error' : '';
    const submitted = fieldErrorName;
    const error = fieldErrorName ? { required: 'O campo é obrigatório' } : '';

    return (
      <Fragment>
        <Grid block>
          <h2 className="mb__md">
            <I18n label="general.content" />
          </h2>

          <InputBlock
            toggleAll
            labelValue="quiz_editor.quiz_types.description"
            className="product__radiolist"
          >
            <Panel header="quiz_editor.quiz_types.title" nopadding>
              <Radiolist
                name="quiz_type"
                items={this.quizTypes}
                selectedOption={type}
                onChange={this.changeQuizType}
              />
            </Panel>
          </InputBlock>

          <InputGroup
            required
            name="name"
            autoComplete="off"
            labelValue="general.name"
            value={page.name}
            onChange={this.changeName}
            placeholder="page_content.name_placeholder"
            className={inputErrorClass}
            submitted={submitted}
            error={error}
            maxLength="100"
          />

          {this.getDescription(maxDescriptionLen, remainingCaracteres)}
          <ContentEditorUploadWrapper
            reduced
            placeholder="general.editor_placeholder"
            value={page.content || ''}
            onChange={this.onContentChange}
            maxLength={maxDescriptionLen}
          />

          <Language
            language={page?.pageLanguage?.language}
            signLanguage={page?.pageLanguage?.signLanguage}
            onLanguageChange={this.changeLanguage}
            onSignLanguageChange={this.changeSignLanguage}
            hasError={languageError}
          />
        </Grid>

        {isDrippingByClassEnabled ? (
          <DrippingList pageType={PageType.Quiz} />
        ) : (
          <Grid block>
            <h2>
              <I18n label="general.configs" />
            </h2>
            <label className="label__subtitle mb__md">
              <I18n label="page_content.configs" />
            </label>

            <Accordion>
              <Accordion.Toggle onlyClose />

              <Accordion.Body>
                <Dripping
                  hasDuration={page.hasDuration}
                  durationDays={page.durationDays}
                  liberationDate={page.liberationDate}
                  liberationDays={page.liberationDays}
                  liberationType={page.liberationType}
                  changeLiberationType={this.changeLiberationType}
                  changeLiberationDays={this.changeLiberationDays}
                  changeHasDuration={this.changeHasDuration}
                  changeLiberationDate={this.changeLiberationDate}
                  changeDurationDays={this.changeDurationDays}
                  errors={drippingErrors}
                />
              </Accordion.Body>

              <Accordion.Toggle />
            </Accordion>
          </Grid>
        )}
      </Fragment>
    );
  }
}
